import React, { useContext, useRef } from 'react';
import { store } from 'components/Store';
import TagsLists from 'components/Dashboard/TagsList';
import useRememberPosition from 'components/Dashboard/hooks/useRememberPosition';
import FeedbackOnItem from 'components/Dashboard/FeedbackItem/FeedbackOnItem';
import GeneralItemActions from 'components/Dashboard/ItemController/GeneralItemActions';
import ContentTypeBadge from 'components/Dashboard/components/ContentTypeBadge';
import PriorityBadge from 'components/Dashboard/ItemController/PriorityBadge';
import {
  ItemControllerActions,
  ListItemContainer,
  ListItemContent,
  ListItemLink,
} from 'components/Dashboard/ListControl/ListItemLayout';
import useListItemController from 'components/Dashboard/ListControl/useListItemController';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import { flattenListRowProps } from 'components/Dashboard/ListControl/listRowProps';
import usePositionBodySnippet from 'components/Dashboard/hooks/usePositionBodySnippet';
import createMarkup from 'components/Dashboard/helpers/createMarkup';
import time from 'utils/time';
import { Link } from 'react-router-dom';

const renderMetaLine = (segments, className = 'heading-date regulation-item-meta mb-0') => {
  const parts = segments.filter(({ value }) => value);
  if (parts.length === 0) return null;
  return (
    <p className={className}>
      {parts.map(({ label, value }, index) => (
        <React.Fragment key={label}>
          {index > 0 && <span className='mx-1'>|</span>}
          <strong>{label}:</strong> {value}
        </React.Fragment>
      ))}
    </p>
  );
};

const RegulationItem = ({ listRow }) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { search } = state;
  const { list, activeSearch } = search;
  const { contentResults } = useContentResults();
  const { Regulations } = contentResults;
  const { currentId } = Regulations ?? {};

  const {
    listContext = {},
    feedOverrides = {},
    display = {},
    interactions = {},
    external = {},
    sectionMeta = {},
  } = listRow ?? {};
  const { index, item, scrollBarRef, array, zIndex } = listContext;
  const { agency, dateTime, title, contentUrl, id, type, govDocumentId, docketUrl, docketTitle, docketType } = item;
  const { explanations, feedItemFlags: propsFeedItemFlags } = feedOverrides;
  const { notShowShare, hideYellowDot, showContentTypeBadge, tagsFixedPosition, onlyEmbargoedTag, tooltipText } =
    display;
  const { itemOnclick, thumbDownProps, itemActionsProps, showPriorityBadge } = interactions;
  const { embedShowTags, itemFromWidget } = external;
  const feedItemFlags = propsFeedItemFlags ?? Regulations?.feedItemFlags ?? [];
  const tagsListsProps = flattenListRowProps(listRow);
  const titleBodyComponent = useRef(null);
  const { bodySnippet } = usePositionBodySnippet({ titleBodyComponent, item: { ...item, body: item?.summary } });
  const { numberOfContributions } = sectionMeta;
  const { unseenConditional, locationDescriptor, alreadyMarkedFeedback, priorityBadgeCondition } =
    useListItemController({
      item,
      index,
      feedItemFlags: propsFeedItemFlags,
      hideYellowDot,
      thumbDownProps,
      showPriorityBadge,
    });

  const { rememberPosition } = useRememberPosition();

  const formattedDate = dateTime ? time(dateTime).formatGeneralDate() : '';
  const showDocketAbove = !!numberOfContributions && !!docketTitle;
  const metaLine1 = renderMetaLine([
    { label: 'Agency', value: agency },
    { label: 'Posted', value: formattedDate },
    { label: 'ID', value: govDocumentId },
  ]);
  const metaLine2 = renderMetaLine(
    [...(!showDocketAbove ? [{ label: 'Docket', value: docketTitle }] : []), { label: 'Type', value: docketType }],
    `heading-date regulation-item-meta mb-0${metaLine1 ? ' mt-1' : ''}`
  );

  return (
    <ListItemLink
      to={locationDescriptor(contentUrl)}
      style={{ zIndex: zIndex ?? (array ? array.length - index : 1) }}
      onClick={(e) => {
        if (scrollBarRef) {
          rememberPosition('Regulations', scrollBarRef, index);
        }
        if (itemOnclick) {
          itemOnclick(e);
        }
      }}
      tooltipText={tooltipText}
    >
      <ListItemContainer
        className={`my-2 content-item regulation-list-item adjustment-unseen-dot-parliamentary${
          currentId === id ? ' active' : ''
        }`}
        as='li'
        unseen={unseenConditional}
      >
        <ListItemContent className='px-4 py-3'>
          <div className='d-flex align-items-baseline'>
            <div className='flex-grow-1 pr-1 pr-sm-0 overflow-hidden'>
              {showContentTypeBadge ? (
                <div className='mt-1 specific-bottom-margin-4 flex-grow-1 text-light-blue blue-icon content-type-badge'>
                  <ContentTypeBadge objectType={type ?? 'RegulationDocument'} name={'Regulation'} />
                </div>
              ) : null}
              {showDocketAbove && <p className='paragraph-p4 text-light-blue mb-2 mt-0 text-truncate'>{docketTitle}</p>}
              <h4 className='mb-2 content-item-title'>{title}</h4>
              {metaLine1}
              {metaLine2}
            </div>
            {!notShowShare && (
              <ItemControllerActions
                className='px-0 justify-content-end flex-centered'
                priorityBadge={
                  priorityBadgeCondition ? <PriorityBadge item={item} feedItemFlags={feedItemFlags[index]} /> : null
                }
                actions={
                  <GeneralItemActions
                    item={item}
                    index={index}
                    thumbDownProps={thumbDownProps}
                    itemActionsProps={itemActionsProps}
                  />
                }
              />
            )}
          </div>
          {!!bodySnippet && (
            <div className='row mx-0 align-items-start legislation-title-container'>
              <div className={`legislation-title w-100 px-0`}>
                <p className='title-body mt-2 mb-0 position-relative' ref={titleBodyComponent}>
                  <span dangerouslySetInnerHTML={createMarkup(bodySnippet)} />
                </p>
              </div>
            </div>
          )}
          {((list !== null && activeSearch && !itemFromWidget) || embedShowTags) && (
            <TagsLists
              {...tagsListsProps}
              explanations={explanations ?? Regulations?.explanations}
              feedItemFlags={feedItemFlags}
              index={index}
              title={'Regulation'}
              section={'Regulations'}
              tagsFixedPosition={tagsFixedPosition}
              scrollBarRef={scrollBarRef}
              onlyEmbargoedTag={onlyEmbargoedTag}
            />
          )}
          {!!numberOfContributions && (
            <div className='pt-2'>
              <Link
                to={docketUrl}
                className='text-light-blue link-hover paragraph-p3 d-inline-block'
                onClick={(e) => {
                  if (!docketUrl) {
                    e.preventDefault();
                  }
                }}
              >
                {numberOfContributions} matching document{numberOfContributions === 1 ? '' : 's'} in docket
              </Link>
            </div>
          )}
        </ListItemContent>

        {alreadyMarkedFeedback && (
          <FeedbackOnItem
            item={item}
            index={index}
            updateFeedback={thumbDownProps?.updateFeedback}
            explanations={thumbDownProps?.explanations}
          />
        )}
      </ListItemContainer>
    </ListItemLink>
  );
};

export default RegulationItem;
